import {
  useIdToken,
  useSignInWithEmailAndPassword,
} from 'react-firebase-hooks/auth';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { auth, auth as firebaseAuth } from '@/app/providers/firebase';
import { useUpdatePasswordMutation } from '@/features/chat/services/user-update';
import { Button, Form, Input, toast } from '@/uikit/components';

import styles from './CreatePassword.module.scss';

interface CreatePasswordProps {
  onSubmit: () => void;
}
export const CreatePassword = ({
  onSubmit,
}: CreatePasswordProps): React.ReactNode => {
  const [user] = useIdToken(firebaseAuth);
  const [signInWithEmailAndPassword] = useSignInWithEmailAndPassword(auth);
  const [updatePassword, { isLoading }] = useUpdatePasswordMutation();
  const { t } = useTranslation();

  const schema = z
    .object({
      password: z
        .string()
        .trim()
        .min(1, t('validation.passwordRequired'))
        .min(6, t('validation.passwordMin')),
      confirmPassword: z
        .string()
        .trim()
        .min(1, t('validation.confirmPasswordRequired'))
        .min(6, t('validation.confirmPasswordMin')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('validation.passwordMatch'),
      path: ['confirmPassword'],
    });

  const handleSubmit = async (event: {
    password: string;
    confirmPassword: string;
  }): Promise<void> => {
    try {
      await updatePassword({
        password: event.confirmPassword,
        userId: user?.uid,
      }).unwrap();

      await signInWithEmailAndPassword(
        user?.email ?? '',
        event.confirmPassword,
      );

      onSubmit();
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    }
  };
  return (
    <div className={styles.container}>
      <Form schema={schema} onSubmit={handleSubmit} mode="onChange">
        <div className={styles.container__form}>
          <Form.Item name="password">
            <Input
              type="password"
              label={t('password')}
              name="password"
              placeholder={t('account.changePassword.newPasswordPlaceholder')}
            />
          </Form.Item>
          <Form.Item name="confirmPassword">
            <Input
              type="password"
              label={t('confirmPassword')}
              name="confirmPassword"
              placeholder={t(
                'account.changePassword.confirmPasswordPlaceholder',
              )}
            />
          </Form.Item>
          <Button
            size="xl"
            type="submit"
            className={styles.container__button}
            loading={isLoading}
          >
            <Trans i18nKey="userUpdate.saveAndContinue">Save & Continue</Trans>
          </Button>
        </div>
      </Form>
    </div>
  );
};
